import { Pipe, PipeTransform } from '@angular/core';
import { Property, PropertySet } from '@vsolv/packages/properties/domain';
import { DIMENSION_VALUE_NAMESPACE } from '@wsphere/warranties/domain';
import { catchError, from, map, Observable, of } from 'rxjs';
import { RatingMatrixService } from '../services';

// Shows a bound value by its friendly name instead of the raw path stored on the rule:
// a scalar dimension's title for `dimension.<id>`, or the property's name otherwise.
// Falls back to the raw path when it can't be resolved (set not loaded, dimension gone).
@Pipe({ name: 'valuePathLabel', standalone: false })
export class ValuePathLabelPipe implements PipeTransform {
  constructor(private readonly ratingMatrixSvc: RatingMatrixService) {}

  transform(valuePropertyPath?: string | null, propertySet?: PropertySet.Model | null): Observable<string> {
    if (!valuePropertyPath) return of('');

    const dimensionPrefix = `${DIMENSION_VALUE_NAMESPACE}.`;
    if (!valuePropertyPath.startsWith(dimensionPrefix)) {
      // Properties are already on the set in memory — resolve the name synchronously.
      return of(this.propertyName(valuePropertyPath, propertySet?.properties) ?? valuePropertyPath);
    }

    // Dimension titles come from the rating endpoint (cached per set by the service).
    const setId = propertySet?.id;
    if (!setId) return of(valuePropertyPath);

    const dimensionId = valuePropertyPath.slice(dimensionPrefix.length);

    // The service is the single cache for the list; on failure fall back to the raw path
    // (matching the pre-pipe behaviour) rather than letting the async binding error.
    return from(this.ratingMatrixSvc.listScalarDimensions(setId)).pipe(
      map(dimensions => dimensions.find(dimension => dimension.id === dimensionId)?.title ?? valuePropertyPath),
      catchError(() => of(valuePropertyPath))
    );
  }

  // Walks the set's properties (including nested objects) for a matching valueKey.
  private propertyName(
    valueKey: string,
    assignments?: { property?: Property.Model<Property.PropertyType> }[]
  ): string | null {
    for (const assignment of assignments ?? []) {
      const property = assignment.property;
      if (!property) continue;
      if (property.valueKey === valueKey) return property.name;
      if (valueKey.startsWith(`${property.valueKey}.`)) {
        const nested = this.propertyName(
          valueKey.slice(property.valueKey.length + 1),
          (property as Property.ObjectModel).properties
        );
        if (nested) return nested;
      }
    }
    return null;
  }
}
